/** 行政区划展示字段，与后端 AdministrativeDivisionDto 对齐 */
export interface BusinessAreaValue {
  countryName?: string;
  countryId?: string;
  countryCode?: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
}

/**
 * 是否为行政区划对象。
 *
 * 变更记录（diff）态会传入已渲染的 ReactNode，另有字符串形态，均需排除，
 * 否则会被当对象取属性而展示为空。
 *
 * 注意：此处刻意不引入 React，保持本模块为可单测的纯函数。
 */
export const isBusinessAreaValue = (value: unknown): value is BusinessAreaValue => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return (
    'provinceName' in value || 'cityName' in value || 'districtName' in value
  );
};

/**
 * 拼接行政区划展示串。
 *
 * 海外供应商的流程只允许选到二级（国家 → 省/州），故海外展示「国家-省/州」；
 * 存量数据常带市/区三级，一并裁掉。境内保持原有三级展示不变（不加国家）。
 *
 * 与后端口径一致：境外出站取 provinceId（`AdministrativeDivisionOaFormNodeProcessor`），
 * 页面展示同样以 provinceName 为准，不做市/区回退 —— 二级缺失时宁可显示 '-'
 * 也不把三级当二级展示。
 *
 * 国家名缺失时（尚未回刷的历史数据）自动退化为原有展示，不显示多余分隔符；
 * 是否加国家只由 isOverseas 决定，与 countryName 是否有值无关。
 *
 * @param value 行政区划对象
 * @param isOverseas 是否海外供应商
 */
export const joinBusinessArea = (
  value: BusinessAreaValue,
  isOverseas: boolean
): string => {
  const parts = isOverseas
    ? [value.countryName, value.provinceName]
    : [value.provinceName, value.cityName, value.districtName];
  return parts.filter(Boolean).join('-') || '-';
};

/** 归一为展示串：非行政区划对象（含 undefined）统一显示 '-' */
const toDisplayText = (value: unknown, isOverseas: boolean): string =>
  isBusinessAreaValue(value) ? joinBusinessArea(value, isOverseas) : '-';

/**
 * 变更记录（diff）态的渲染决策。
 *
 * 包内默认 renderFormItem 的 diff 分支直接用原始值走包内 BusinessArea 渲染，
 * **不经过调用方传入的 render**，因此变更记录必须单独覆盖才能生效。
 * 此处把「按 mode 决定注入哪些值」的语义抽为纯函数，便于单测锁定。
 */
export type DiffRenderDecision =
  /** diff-change：新旧值都要格式化后注入 */
  | { kind: 'change'; oldValue: string; newValue: string }
  /** diff-add / diff-delete：单值格式化后注入 */
  | { kind: 'single'; value: string }
  /** 非 diff 态：不接管，交回包内默认实现 */
  | { kind: 'passthrough' };

/**
 * 依据渲染 mode 决定变更记录态注入的值。
 *
 * @param mode 渲染模式（ProField 传入）
 * @param props 宿主节点上的原始值
 * @param isOverseas 是否海外供应商
 */
export const decideDiffRender = (
  mode: string | undefined,
  props: { oldValue?: unknown; newValue?: unknown; value?: unknown },
  isOverseas: boolean
): DiffRenderDecision => {
  if (mode === 'diff-change') {
    return {
      kind: 'change',
      oldValue: toDisplayText(props?.oldValue, isOverseas),
      newValue: toDisplayText(props?.newValue, isOverseas),
    };
  }
  if (mode === 'diff-add' || mode === 'diff-delete') {
    return { kind: 'single', value: toDisplayText(props?.value, isOverseas) };
  }
  return { kind: 'passthrough' };
};
